import { useState, useMemo, useRef } from 'react';
import { useCombobox } from 'downshift';
import debounce from 'lodash.debounce';
import { faSearch, faTimes } from '@fortawesome/pro-light-svg-icons';

import IconButton from '@library/IconButton/IconButton';

import { useTranslation } from '@utils/i18n/i18n.consumers';

import './search.styl';

function Search(props) {
  const { t } = useTranslation();
  const { contrast, inputDebounceMs = 500, dataSources } = props;
  const [items, setItems] = useState([]);
  const inputElRef = useRef();
  const onInputValueChange = useMemo(() => debounce(({ inputValue }) => {
    setItems([]);

    if (!inputValue || (inputValue.length < 2)) {
      return;
    }

    dataSources.forEach(async (dataSource) => {
      const { index, onSearch } = dataSource;

      const dataSourceItems = await onSearch(inputValue);

      setItems((prevItems) => {
        return [
          ...prevItems.filter(item => (item.sourceIndex !== index)),
          ...dataSourceItems.map(item => ({ ...item, sourceIndex: index })),
        ].sort((x, y) => (x.sourceIndex - y.sourceIndex));
      });
    })
  }, inputDebounceMs), [inputDebounceMs, dataSources]);
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
  } = useCombobox({
    items,
    itemToString: item => (item ? item.label : ''),
    onInputValueChange,
    onSelectedItemChange: ({ selectedItem: newlySelectedItem }) => {
      // when selection is cleared, newlySelectedItem will be null 
      const { sourceIndex } = (newlySelectedItem || selectedItem);
      const dataSource = dataSources.find(curr => (curr.index === sourceIndex));

      dataSource.onItemSelect(newlySelectedItem, selectedItem);
    },
  })

  return (
    <div className={`search ${contrast ? 'search--contrast' : ''}`}>
      <div className="search__query">
        <input {...getInputProps({ className: 'search__query__input', ref: inputElRef, placeholder: t('Search...') })} />
        <IconButton 
          className="search__query__input__icon-button"
          icon={!selectedItem ? faSearch : faTimes}
          iconColor={contrast ? '#FFF' : '#333'}
          ariaLabel={t('Search')}
          noHoverEffect
          onClick={() => (!selectedItem ? inputElRef.current.focus() : selectItem(null))}
        />
      </div>
      <ul {...getMenuProps({ className: 'search__menu z-depth-1' })}>
        { isOpen
          && items.map((item, index) => (
            <li
              key={`${item.sourceIndex}-${item.id}`}
              {...getItemProps({
                className: `
                    search__menu__item
                    ${(highlightedIndex === index) ? 'search__menu__item--highlighted' : ''}
                    ${(selectedItem && (selectedItem.sourceIndex === item.sourceIndex) && (selectedItem.id === item.id)) ? 'search__menu__item--selected' : ''}
                    ellipsis
                  `,
                item,
                index,
              })
              }
            >
              <span>{item.label}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}

export default Search;
